import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, map, Observable, of, Subscription } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  format,
  parse,
  addDays,
  isPast,
  differenceInCalendarDays,
  parseISO,
  differenceInDays,
} from 'date-fns';

import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { DataService } from 'src/app/services/data.service';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { AppointmentInfo, EventData } from 'src/app/services/data.model';
import { FormData } from 'src/app/services/data.model';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { TimerService } from 'src/app/services/timer.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { PplCardComponent } from 'src/app/shared/components/ppl-card/ppl-card.component';
import { TimerComponent } from 'src/app/shared/components/timer/timer.component';

interface TimeSlot {
  startTime: string;
  id: string;
  endTime: string;
  periodOfTheDay: string;
  hide?: boolean;
}

interface TimeSlotExt extends TimeSlot {
  time: string;
}

interface TimeBlock {
  period: string;
  slots: TimeSlotExt[];
}

interface AppointmentItem {
  date: string;
  dayOfTheWeek: string;
  timeBlocks: TimeSlot[];
}

interface AppointmentItemExt extends AppointmentItem {
  formattedDate: {
    day: string;
    month: string;
  };
  timeSlots: TimeBlock[];
  disabled: boolean;
}

@Component({
  selector: 'app-time-date-booking',
  standalone: true,
  imports: [
    CommonModule,
    MainLayoutComponent,
    ReactiveFormsModule,
    PplCardComponent,
    TimerComponent,
  ],
  templateUrl: './time-date-booking.component.html',
  styleUrl: './time-date-booking.component.scss',
})
export class TimeDateBookingComponent implements OnInit, OnDestroy {
  scheduleForm: FormGroup;
  calendarData = {};
  periods = ['Morning', 'Afternoon', 'Evening'];

  activeDate = format(new Date(), 'yyyy-MM-dd');
  selectedTime = ''; // time ID
  startDay = format(new Date(), 'yyyy-MM-dd');
  previouslyFilledData: AppointmentInfo;
  activeChunkNumber = 0;
  isDataLoaded = false;
  numberOfDays = 14;
  chunkSize = 7;
  numberOfChunks = Math.ceil(this.numberOfDays / this.chunkSize);
  isPPL = false;
  showNotification: boolean = false;
  fadeClass: string = '';
  displayedPromotion: boolean = false;
  private timeoutId: any;
  subscriptions: Subscription = new Subscription();
  isEvent = false;
  offer: string;
  windowCount;
  skipTimeSlotPrepopulating = false;
  selectedTimeId: string | undefined;
  activeSlots: TimeBlock[];
  noSlotsAvailableForThisDay = false;
  formattedActiveDate: string;
  firstDateIndexWithSlots = 0;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private timerService: TimerService,
    private navigationService: NavigationService,
    public nextButtonState: NextButtonStateService,
  ) {
    this.checkEventFlow();
    this.getPromotionalOffers();
  }

  ngOnInit() {
    this.scheduleForm = this.fb.group({
      selectedTime: ['', Validators.required],
      selectedTimeDate: ['', Validators.required],
      selectedStartTime: [''],
      selectedEndTime: [''],
      noAvailableTimeSlots: [false],
      noneWorksForMe: [false],
    });

    const timerOkButtonSub = this.timerService.okButtonClick$.subscribe(() => {
      this.navigationService.navigateToPath('time-date-booking');
      this.unselectTime();
    });
    this.subscriptions.add(timerOkButtonSub);
    if (this.dataService.id) {
      const stepsDatasub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          if (data?.appointmentRequest?.isPPL) {
            this.isPPL = !!data?.appointmentRequest?.isPPL;
            if (!this.isEvent) {
              this.numberOfDays = 7;
              this.numberOfChunks = Math.ceil(
                this.numberOfDays / this.chunkSize
              );
            }
          }
          this.windowCount = data?.appointmentRequest?.project?.windowCount;
          const sessionData = JSON.parse(data.sessionData);
          this.selectedTimeId = sessionData?.appointmentInfo?.selectedTime;
          this.getCalendarData(
            this.numberOfDays,
            data?.appointmentRequest?.property?.zipcode
          );
          this.prePopulateStepData(data);
          this.displayedPromotion =
            !!data?.appointmentRequest?.appointment?.schedulingPromotion
              ?.displayedPromotion;
        });
      this.subscriptions.add(stepsDatasub);
    }
  }

  checkEventFlow() {
    const eventSub = this.navigationService
      .getEventData()
      .subscribe((event: EventData) => {
        this.isEvent = !!event?.isEvent;
      });
    this.subscriptions.add(eventSub);
  }

  async getCalendarData(numberOfDays: number, zipCode: string) {
    const response = await lastValueFrom(
      this.makeApiRequest(this.startDay, zipCode, numberOfDays)
    );
    const allAppointmentBlocks = response.appointmentBlocks;

    const chunks = [];
    for (let i = 0; i < allAppointmentBlocks.length; i += this.chunkSize) {
      const chunk = allAppointmentBlocks.slice(i, i + this.chunkSize);
      chunks.push(chunk);
    }

    chunks.forEach((chunk, index) => {
      this.calendarData[index] = this.prepareDays(chunk);
    });
    this.findActiveChunk();
    this.checkIfNoSlots();
    this.adjustDefaultActiveDate();
  }

  adjustDefaultActiveDate() {
    if (!this.selectedTime) {
      const date = this.calendarData?.[0]?.[0]?.date;
      this.activeDate = format(
        addDays(parseISO(date), this.defineFirstDateIndexWithSlots()),
        'yyyy-MM-dd'
      );
      this.updateSlots();
    }
  }

  defineFirstDateIndexWithSlots(): number {
    const day: AppointmentItem[] | undefined = this.calendarData?.[0];
    if (!Array.isArray(day)) return 0;

    for (let i = 0; i < day.length; i++) {
      const blocks = day[i]?.timeBlocks ?? [];
      if (blocks.some((slot) => slot && slot.hide !== true)) {
        return i;
      }
    }
    return 0;
  }

  updateSlots() {
    this.activeSlots =
      this.calendarData?.[this.activeChunkNumber]?.find(
        (el: AppointmentItem) => el.date === this.activeDate
      )?.timeSlots || [];

    const hasSlots = this.activeSlots.some(
      (period) => !this.noSlotsForSelectedPeriod(period)
    );

    this.noSlotsAvailableForThisDay =
      !hasSlots || this.activeSlots.length === 0;
    this.formattedActiveDate = format(
      parseISO(this.activeDate),
      'EEEE, MMMM do'
    );
  }

  updateFormValues() {
    this.scheduleForm.patchValue({
      selectedTime: this.previouslyFilledData?.selectedTime || '',
      selectedTimeDate: this.previouslyFilledData?.selectedTimeDate || '',
      selectedStartTime: this.previouslyFilledData?.selectedStartTime || '',
      selectedEndTime: this.previouslyFilledData?.selectedEndTime || '',
      noAvailableTimeSlots:
        this.previouslyFilledData?.noAvailableTimeSlots || false,
      noneWorksForMe: this.previouslyFilledData?.noneWorksForMe || false,
    });
    this.checkFormValidity();
  }

  prePopulateStepData(data: FormData) {
    const sessionData = JSON.parse(data.sessionData);
    if (
      sessionData.appointmentInfo &&
      sessionData.appointmentInfo.selectedTimeDate
    ) {
      // validate if the previously selected date is in the past and at least 1 day
      const past = isPast(
        `${sessionData.appointmentInfo?.selectedTimeDate}T${sessionData.appointmentInfo?.selectedEndTime}`
      );
      const diff = differenceInCalendarDays(
        this.activeDate,
        sessionData.appointmentInfo?.selectedTimeDate
      );
      if (!past || (past && diff < 1)) {
        this.previouslyFilledData = sessionData.appointmentInfo;
        this.updateFormValues();
        this.selectedTime = this.previouslyFilledData?.selectedTime || '';
        this.activeDate = this.previouslyFilledData?.selectedTimeDate || '';
        this.findActiveChunk();
        this.checkIfNoSlots();
        this.updateSlots();
      }
    }
    this.isDataLoaded = true;
    this.subscribeToFormChanges();
  }

  findActiveChunk() {
    Object.keys(this.calendarData).forEach((key) => {
      const isActiveChunk = this.calendarData[key].some(
        (el: AppointmentItem) => el.date === this.activeDate
      );
      if (isActiveChunk) {
        this.activeChunkNumber = Number(key);
        this.updateSlots();
      }
    });
  }

  subscribeToFormChanges(): void {
    const scheduleFormSub = this.scheduleForm.valueChanges.subscribe(
      (value) => {
        this.checkFormValidity();
      }
    );
    this.subscriptions.add(scheduleFormSub);
  }

  checkFormValidity() {
    setTimeout((_) => {
      if (
        !this.scheduleForm.valid &&
        (this.scheduleForm.controls['noAvailableTimeSlots'].value ||
          this.scheduleForm.controls['noneWorksForMe'].value)
      ) {
        this.nextButtonState.emitActivateNext(true);
        this.nextButtonState.emitActivateNoTimeslotsWorkButton(false);
      } else {
        this.nextButtonState.emitActivateNext(this.scheduleForm.valid);
        this.nextButtonState.emitActivateNoTimeslotsWorkButton(
          !this.scheduleForm.valid
        );
      }
    });
  }

  selectTime(period: { id: string; startTime: string; endTime: string }): void {
    const day = this.calendarData?.[this.activeChunkNumber]?.find(
      (el: AppointmentItem) => el.date === this.activeDate
    );
    if (!day.disabled) {
      this.activeDate = day.date;
    }
    if (!this.isEvent) {
      this.timerService.stop();
      this.timerService.removeAlert();
      this.timerService.start();
    }
    this.selectedTime = `${period.id}_${period.startTime}`;
    this.scheduleForm.patchValue({
      selectedTime: `${period.id}_${period.startTime}`,
      selectedTimeDate: this.activeDate,
      selectedStartTime: period.startTime,
      selectedEndTime: period.endTime,
      noAvailableTimeSlots: false,
      noneWorksForMe: false,
    });
    this.checkSelectedDay();
    this.updateSlots();
  }

  unselectTime() {
    this.selectedTime = '';
    this.scheduleForm.patchValue({
      selectedTime: '',
      selectedTimeDate: '',
      selectedStartTime: '',
      selectedEndTime: '',
    });
  }

  checkSelectedDay() {
    const timeToShow = 60000; // 1 minute
    if (
      this.isDateBeyond3Days() &&
      !(
        this.displayedPromotion ||
        this.dataService.stepsData?.appointmentRequest?.appointment
          ?.schedulingPromotion?.displayedPromotion
      )
    ) {
      this.toggleNotification();
      this.timeoutId = setTimeout((_) => {
        this.toggleNotification();
      }, timeToShow);
    }
  }

  cancelNotificationTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  isDateBeyond3Days() {
    const selectedDate = parseISO(this.scheduleForm.value.selectedTimeDate);
    const startDate = parseISO(this.startDay);
    const diffInDays = differenceInDays(selectedDate, startDate);

    return !!(diffInDays > 2);
  }

  toggleNotification() {
    if (this.showNotification) {
      this.fadeClass = 'fade-out';
      setTimeout(() => {
        this.showNotification = false;
      }, 500);
    } else {
      this.showNotification = true;
      this.displayedPromotion = true;
      setTimeout((_) => {
        this.fadeClass = 'fade-in';
      });
    }
  }

  setActiveDay(day: { date: string; disabled: boolean }): void {
    if (!day.disabled) {
      if (day.date !== this.activeDate) {
        this.unselectTime();
        this.activeDate = day.date;
        this.updateSlots();
      }
    }
  }

  checkIfNoSlots(): void {
    let slotsCount = 0;
    let dayNumber = 1;
    Object.keys(this.calendarData).forEach((key) => {
      this.calendarData[key].forEach((day) => {
        if (dayNumber <= this.numberOfDays) {
          if (day && day.timeBlocks && day.timeBlocks.length) {
            slotsCount = slotsCount + day.timeBlocks.length;
          }
          dayNumber++;
        }
      });
    });
    if (!slotsCount) {
      this.scheduleForm.patchValue({ noAvailableTimeSlots: true });
    } else {
      this.scheduleForm.patchValue({ noAvailableTimeSlots: false });
    }
  }

  goForward(): void {
    this.unselectTime();
    this.activeChunkNumber = this.activeChunkNumber + 1;
    this.activeDate = this.calendarData[this.activeChunkNumber][0].date;
    this.updateSlots();
  }

  goBackward(): void {
    this.unselectTime();
    this.activeChunkNumber = this.activeChunkNumber - 1;
    this.activeDate = this.calendarData[this.activeChunkNumber][0].date;
    this.updateSlots();
  }

  onClickNext = (force?: boolean): Observable<any> => {
    if (force) {
      return this.saveData(
        NAVIGATION_MAP[PAGES.TIME_DATE_BOOKING].nextButton.route,
        true
      );
    } else if (this.scheduleForm.valid) {
      return this.saveData(
        NAVIGATION_MAP[PAGES.TIME_DATE_BOOKING].nextButton.route,
        true
      );
    } else {
      this.scheduleForm.markAllAsTouched();
      return of(false);
    }
  };

  onClickBack = (): Observable<any> => {
    return this.scheduleForm.valid
      ? this.saveData(NAVIGATION_MAP[PAGES.TIME_DATE_BOOKING].backButton.route)
      : of(true);
  };

  saveData(nextPage, isNext?): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };

    const sessionData = JSON.parse(stepsData.sessionData);
    let formattedDate = '';
    if (
      this.scheduleForm.value.selectedTimeDate &&
      this.scheduleForm.value.selectedStartTime
    ) {
      const dateTimeString = `${this.scheduleForm.value.selectedTimeDate}T${this.scheduleForm.value.selectedStartTime}`;
      const parsedDate = parseISO(dateTimeString);
      formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss.SSS");
    }

    sessionData.nextPage = nextPage;
    sessionData.appointmentInfo = this.scheduleForm.value;
    stepsData.sessionData = JSON.stringify(sessionData);
    if (this.scheduleForm.value.noneWorksForMe) {
      stepsData.appointmentRequest.appointment = null;
    } else {
      stepsData.appointmentRequest.appointment = {
        appointmentDateTime: formattedDate,
        appointmentBlockGuid:
          this.scheduleForm.value.selectedTime.split('_')[0],
        schedulingPromotion: {
          displayedPromotion:
            stepsData?.appointmentRequest?.appointment?.schedulingPromotion
              ?.displayedPromotion || this.displayedPromotion,
          acceptedPromotion:
            this.displayedPromotion && !this.isDateBeyond3Days(),
        },
      };
    }
    if (isNext) {
      stepsData.status = stepsData.appointmentRequest.appointment
        ? 'Completed'
        : 'CallToSet';
    }
    stepsData.appointmentRequest.projectDetails = null;
    if (!stepsData?.appointmentRequest?.insideSales) {
      stepsData.appointmentRequest.insideSalesReasonEnum = 0;
    }

    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        if (isNext && this.timerService) this.timerService.stop();
        return true;
      })
    );
  }

  private makeApiRequest(
    date: string,
    code: string,
    numberOfDays: number
  ): Observable<any> {
    return this.dataService
      .getTimeBookingData(date, code, numberOfDays, this.isEvent)
      .pipe(
        map((data: { appointmentBlocks: AppointmentItem[] }) => {
          return data;
        })
      );
  }

  groupByPeriodOfTheDay(array: TimeSlot[]): {
    [key: string]: TimeSlotExt[];
  } {
    return array.reduce((result, currentItem) => {
      const period = currentItem.periodOfTheDay;
      if (!result[period]) {
        result[period] = [];
      }
      const date = parse(currentItem.startTime, 'HH:mm:ss', new Date());
      const formattedTime = format(date, 'hh:mm a');
      currentItem['time'] = formattedTime;
      result[period].push(currentItem);
      return result;
    }, {});
  }

  ngOnDestroy() {
    if (this.subscriptions) this.subscriptions.unsubscribe();
    this.cancelNotificationTimeout();
  }

  prepareDays(appointmentBlocks) {
    return appointmentBlocks.map((el: AppointmentItem): AppointmentItemExt => {
      const timeBlocks = this.groupByPeriodOfTheDay(el.timeBlocks);

      if (!this.isEvent) {
        el['timeBlocks'].forEach((day: TimeSlot) => {
          if (
            (el.dayOfTheWeek === 'Sun' && this.windowCount < 6) ||
            (this.windowCount < 3 &&
              (day.periodOfTheDay === 'Evening' || el.dayOfTheWeek === 'Sat'))
          ) {
            if (day.id === this.selectedTimeId) {
              this.unselectTime();
              this.activeChunkNumber = 0;
              this.activeDate =
                this.calendarData[this.activeChunkNumber][0].date;
              this.updateSlots();
            }
            day.hide = true;
          }
        });
      }

      return {
        ...el,
        formattedDate: {
          day: format(parseISO(el.date), 'd'),
          month: format(parseISO(el.date), 'LLL'),
        },
        timeSlots: Object.keys(timeBlocks).map((key) => ({
          period: key,
          slots: timeBlocks[key],
        })),
        disabled: this.isDisabledDay(el),
      };
    });
  }

  noSlotsForSelectedPeriod(day: any): boolean {
    return (
      !day?.slots?.length || day.slots.every((slot: TimeSlot) => slot.hide)
    );
  }

  noneWorksForMe = (): any => {
    this.timerService.stop();
    this.timerService.removeAlert();
    this.scheduleForm.patchValue({
      noneWorksForMe: true,
      selectedTime: '',
      selectedTimeDate: '',
      selectedStartTime: '',
      selectedEndTime: '',
    });
    const nextButtonSub = this.onClickNext(true).subscribe((res) => {
      this.navigationService.navigateToPath(PAGES.ADDITIONAL_COMMENTS);
    });
    this.subscriptions.add(nextButtonSub);
  };

  isDisabledDay(day: AppointmentItem): boolean {
    const diff = differenceInCalendarDays(day.date, this.startDay);
    if (diff < this.numberOfDays) {
      return false;
    }
    return true;
  }

  getPromotionalOffers() {
    const sub = this.dataService
      .getPromotionalOffers()
      .subscribe((offer: string) => {
        if (offer) this.offer = offer;
      });
    this.subscriptions.add(sub);
  }
}
